import React from "react";
import Badge from "../../../../components/elements/Badge";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { decrypt, encrypt } from "../../../../helpers";
import { useQuery } from "@tanstack/react-query";
import { accountCheck } from "../../../../services/eventService";
import { organizationDetails } from "../../../../services/organizationService";
import Button from "../../../../components/form/Button";
import { useSelector } from "react-redux";

const PaymentVerificationSkeleton = () => {
  return (
    <div className="flex items-center w-screen h-screen justify-center bg-slate-50 gap-5 animate-pulse">
      <div className="overflow-hidden w-[500px] h-[300px] rounded-lg bg-gray-200" />

      <div className="w-fit h-fit flex flex-col items-center">
        <div className="h-6 w-32 bg-gray-200 rounded-full" />

        <div className="mt-10 space-y-3 w-[300px]">
          <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto" />

          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-5/6 mx-auto" />
          </div>
        </div>

        <div className="mt-5 space-y-5 rounded-lg bg-white p-7 w-[300px] border border-gray-100">
          <div className="h-5 bg-gray-200 rounded w-1/2" />
          <ul className="space-y-3">
            <li className="flex gap-2 items-start">
              <span className="w-2 h-2 rounded-full bg-gray-300 mt-[7px]" />
              <div className="h-4 bg-gray-200 rounded w-full" />
            </li>
            <li className="flex gap-2 items-start">
              <span className="w-2 h-2 rounded-full bg-gray-300 mt-[7px]" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const PaymentStatus = () => {
  const navigate = useNavigate();
  const { lang } = useSelector((state) => state?.langProvider);
  const { session } = useSelector((state) => state?.authProvider);
  const { data } = useSelector(
    (state) => state?.organization?.selectedOrganizationDetails
  );
  const location = useLocation();
  const params = new URLSearchParams(location?.search);
  const merchantIdInPayPal = params.get("merchantIdInPayPal");
  const paypal_data = session?.user?.paymentAccounts?.find(
    (ac) => ac?.metadata?.resource?.merchant_id === merchantIdInPayPal
  );
  const isPaypalConnect =
    paypal_data?.metadata?.resource?.merchant_id === merchantIdInPayPal;

  const {
    data: accountData,
    isLoading: isAccountLoading,
    refetch: refetchAccountData,
  } = useQuery({
    queryKey: ["account-status-status", session?.user?._id],
    queryFn: async () => {
      const res = await accountCheck({
        user: session?.user?._id,
      });

      return res?.data?.data;
    },
    enabled: !!session?.user?._id,
  });

  return isAccountLoading ? (
    <PaymentVerificationSkeleton />
  ) : (
    <div className="w-screen h-screen bg-slate-50">
      <div className="flex flex-col lg:flex-row gap-14 w-full h-full items-center">
        <div className="w-full h-auto lg:h-full flex justify-center items-center bg-gradient-to-tl from-slate-100 to-slate-200/80 shadow-lg shadow-purple-200 relative overflow-hidden p-12 lg:p-0">
          <div className="absolute top-[10%] left-[8%] w-[15%] aspect-square rounded-full bg-purple-300/25 animate-bubble1"></div>
          <div className="absolute bottom-[15%] right-[10%] w-[20%] aspect-square rounded-full bg-orange-300/25 animate-bubble2"></div>
          <div className="absolute top-[80%] right-[80%] w-[25%] aspect-square rounded-full bg-purple-400/25 animate-bubble3"></div>
          <div className="absolute bottom-[10%] left-[40%] w-[10%] aspect-square rounded-full bg-orange-400/20 animate-bubble4"></div>
          <div className="absolute -top-[10%] left-[66%] w-[20%] aspect-square rounded-full bg-orange-400/20 animate-bubble5"></div>
          <div className="absolute top-[18%] right-[5%] w-[9%] aspect-square rounded-full bg-purple-300/20 animate-bubble6"></div>

          <div className="overflow-hidden w-[300px] md:w-[500px] aspect-square rounded-lg z-10 shadow-xl">
            <img
              src={require("../../../../assets/images/payment-3.jpg")}
              alt="payment-img"
            />
          </div>
        </div>
        <div className="w-full flex flex-col items-start px-6 lg:pr-5 lg:pl-0">
          <Badge
            label={
              (accountData?.stripe &&
                Object.keys(accountData?.stripeData)?.length > 0) ||
              (isPaypalConnect && accountData?.paypal)
                ? "Connected"
                : accountData?.stripe === false &&
                  Object.keys(accountData?.stripeData)?.length === 0 &&
                  !isPaypalConnect
                ? "Progress in pending"
                : "Verification Pending"
            }
            type={"info"}
          />
          <div className="mt-5 max-w-lg">
            <h3 className="font-bold text-3xl lg:text-5xl !leading-snug mb-5">
              Payment Account Verification
            </h3>
            <div className="text-base lg:text-lg text-surface-05 leading-relaxed mb-6">
              {!isPaypalConnect &&
              accountData?.stripe &&
              Object.keys(accountData?.stripeData)?.length > 0 ? (
                "Your stripe payment account has been successfully verified and connected."
              ) : isPaypalConnect && accountData?.paypal ? (
                "Your paypal payment account has been successfully verified and connected."
              ) : accountData?.stripe === false &&
                Object.keys(accountData?.stripeData)?.length === 0 &&
                !isPaypalConnect ? (
                <>
                  Your payment account setup hasn&apos;t started yet.
                  <br /> We&apos;ll notify you once verification begins.
                </>
              ) : (
                <>
                  Thank you for submitting your Stripe form. We’re currently
                  reviewing your payment account details to ensure everything is
                  set up correctly. You’ll receive a confirmation once the
                  verification process is complete.
                </>
              )}
            </div>
          </div>
          {accountData?.stripe &&
            Object.keys(accountData?.stripeData)?.length === 0 && (
              <div className="mt-5 space-y-5 rounded-lg bg-white p-7">
                <h3 className="font-medium text-lg">What happens next?</h3>
                <ul className="space-y-3">
                  <li className="flex gap-2 items-start text-sm font-[400]">
                    <span className="flex w-2 min-w-2 h-2 rounded-full bg-deep-purple mt-[7px]"></span>
                    Our security team will verify your identity and account
                    information
                  </li>
                  <li className="flex gap-2 items-start text-sm font-[400]">
                    <span className="flex w-2 min-w-2 h-2 rounded-full bg-deep-purple mt-[7px]"></span>
                    Once approved, you can start accepting payments immediately
                  </li>
                </ul>
              </div>
            )}
          <div className="mt-2">
            <Button
              buttonLabel={"Go to payment settings"}
              buttonFunction={() => {
                navigate(
                  data?.uid
                    ? `/${lang}/user/organization/settings/${data?.uid}?sec=payment`
                    : `/${lang}/user/organization/overview`
                );
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatus;
